import { useCallback, useEffect, useMemo, useState } from 'react';
import { userManagementApi } from '../api/userManagementApi';
import {
  DepotSummary,
  TenantProfile,
  UserAccessProfile,
  UserAccount,
  isAppAdmin,
  isTenantAdmin,
} from '../models/userManagement';

interface UseUserManagementDataResult {
  tenants: TenantProfile[];
  users: UserAccount[];
  depots: DepotSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useUserManagementData = (
  profile: UserAccessProfile | null
): UseUserManagementDataResult => {
  const [tenants, setTenants] = useState<TenantProfile[]>([]);
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [depots, setDepots] = useState<DepotSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = useMemo(() => isAppAdmin(profile) || isTenantAdmin(profile), [profile]);

  const refresh = useCallback(async () => {
    if (!profile || !enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [tenantList, userList, depotList] = await Promise.all([
        userManagementApi.listTenants(),
        userManagementApi.listUsers(),
        userManagementApi.listDepots(),
      ]);

      if (isAppAdmin(profile)) {
        setTenants(tenantList);
        setUsers(userList);
        setDepots(depotList);
      } else if (isTenantAdmin(profile) && profile.tenantId) {
        setTenants(tenantList.filter((tenant) => tenant.id === profile.tenantId));
        setUsers(userList.filter((user) => user.tenantId === profile.tenantId));
        setDepots(depotList.filter((depot) => depot.tenantId === profile.tenantId));
      } else {
        setTenants([]);
        setUsers([]);
        setDepots([]);
      }
    } catch (err) {
      console.error('[useUserManagementData] Failed to fetch data', err);
      const message = err instanceof Error ? err.message : 'Unable to load management data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [profile, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { tenants, users, depots, loading, error, refresh };
};
